import type {
  TrophyProduct,
  TrophyQuote,
  TrophyQuoteCustomItem,
  TrophyQuoteExtraItem,
  CreateTrophyProductRequest,
} from '../types/trophies';
import { apiRequest } from './api-client';

const TROPHIES = '/backoffice/trophies';

// ===== Products =====

export async function fetchProducts(token: string, params?: { search?: string; includeInactive?: boolean }): Promise<TrophyProduct[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.includeInactive) query.set('includeInactive', 'true');
  return apiRequest(`${TROPHIES}/products?${query}`, token);
}

export async function fetchProduct(id: string, token: string): Promise<TrophyProduct> {
  return apiRequest(`${TROPHIES}/products/${id}`, token);
}

export async function createProduct(data: CreateTrophyProductRequest, token: string): Promise<TrophyProduct> {
  return apiRequest(`${TROPHIES}/products`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProduct(id: string, data: Partial<CreateTrophyProductRequest>, token: string): Promise<TrophyProduct> {
  return apiRequest(`${TROPHIES}/products/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProduct(id: string, token: string): Promise<void> {
  await apiRequest(`${TROPHIES}/products/${id}`, token, { method: 'DELETE' });
}

export async function toggleProductStatus(id: string, token: string): Promise<TrophyProduct> {
  return apiRequest(`${TROPHIES}/products/${id}/status`, token, { method: 'PATCH' });
}

// ===== Quotes =====

export async function fetchQuotes(token: string, params?: { search?: string; status?: string; startDate?: string; endDate?: string }): Promise<TrophyQuote[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return apiRequest(`${TROPHIES}/quotes?${query}`, token);
}

export async function fetchQuote(id: string, token: string): Promise<TrophyQuote> {
  return apiRequest(`${TROPHIES}/quotes/${id}`, token);
}

export interface CreateTrophyQuoteRequest {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerCompany?: string;
  customerDocument?: string;
  customerSegment?: string;
  notes?: string;
}

export async function createQuote(data: CreateTrophyQuoteRequest, token: string): Promise<TrophyQuote> {
  return apiRequest(`${TROPHIES}/quotes`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteStatus(id: string, status: string, token: string): Promise<TrophyQuote> {
  return apiRequest(`${TROPHIES}/quotes/${id}/status`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function markTrophyQuoteAsViewed(quoteId: string, token: string): Promise<TrophyQuote> {
  return apiRequest(`${TROPHIES}/quotes/${quoteId}/viewed`, token, {
    method: 'POST',
  });
}

// ===== Quote Item Management =====

export async function addQuoteItem(quoteId: string, data: { productId: string; quantity: number; dimensionId?: string }, token: string): Promise<any> {
  return apiRequest(`${TROPHIES}/quotes/${quoteId}/items`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteItem(itemId: string, data: { quantity: number }, token: string): Promise<any> {
  return apiRequest(`${TROPHIES}/quotes/items/${itemId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteQuoteItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${TROPHIES}/quotes/items/${itemId}`, token, { method: 'DELETE' });
}

// ===== Custom Items =====

export async function fetchPendingCustomItems(token: string): Promise<TrophyQuoteCustomItem[]> {
  return apiRequest(`${TROPHIES}/quotes/pending-custom-items`, token);
}

export interface ConfirmCustomItemPricePayload {
  confirmedPrice?: number;
}

export async function confirmCustomItemPrice(itemId: string, payload: ConfirmCustomItemPricePayload, token: string): Promise<TrophyQuoteCustomItem> {
  return apiRequest(`${TROPHIES}/quotes/custom-items/${itemId}/confirm-price`, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteCustomItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${TROPHIES}/quotes/custom-items/${itemId}`, token, { method: 'DELETE' });
}

// ===== Quote Extra Items =====

export async function addExtraItem(quoteId: string, description: string, value: number, token: string): Promise<TrophyQuoteExtraItem> {
  return apiRequest(`${TROPHIES}/quotes/${quoteId}/extra-items`, token, {
    method: 'POST',
    body: JSON.stringify({ description, value }),
  });
}

export async function deleteExtraItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${TROPHIES}/quotes/extra-items/${itemId}`, token, { method: 'DELETE' });
}

export async function updateExtraItems(
  quoteId: string,
  items: Array<{ description: string; value: number }>,
  token: string
): Promise<void> {
  await apiRequest(`${TROPHIES}/quotes/${quoteId}/extra-items`, token, {
    method: 'PUT',
    body: JSON.stringify(items),
  });
}

// ===== Quote Discount =====

export async function updateQuoteDiscount(quoteId: string, discount: string, token: string): Promise<TrophyQuote> {
  return apiRequest(`${TROPHIES}/quotes/${quoteId}/discount`, token, {
    method: 'PATCH',
    body: JSON.stringify({ discount }),
  });
}
